import { ImportanceBadge } from '@/components/ui/importance-badge';
import { ConfidenceBadge } from '@/components/ui/confidence-badge';
import { SourceLinks } from '@/components/ui/source-links';
import { Sparkles, Lightbulb } from 'lucide-react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';
import { DiscoveryCard } from './discovery-card';
import culturalThemeThumbnail from '@/assets/cultural-theme-thumbnail.png';
import { cn } from '@/lib/utils';

interface CulturalThemeCardProps {
    theme: {
        title: string;
        summary: string;
        description: string;
        relevance: string;
        importance?: Importance;
        confidence?: Confidence;
        sources?: Source[];
    };
    isExpanded: boolean;
    onToggleExpansion: () => void;
}

export function CulturalThemeCard({
    theme,
    isExpanded,
    onToggleExpansion,
}: CulturalThemeCardProps) {
    const isDarkMode = useDarkMode();

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            icon={Lightbulb}
            thumbnailUrl={culturalThemeThumbnail}
            header={
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 flex-1 relative z-10">
                    <div className="flex flex-col gap-3 flex-1 min-w-0">
                        <h4 className="text-xl font-serif font-medium tracking-tight text-foreground/90 leading-tight">
                            {theme.title}
                        </h4>

                        {theme.summary && !isExpanded && (
                            <div className="space-y-3">
                                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-4 italic font-light">
                                    {theme.summary}
                                </p>
                                <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-primary/40 group-hover/card:text-primary/70 transition-colors">
                                    <Sparkles className="h-2.5 w-2.5" />
                                    <span>Discover Theme</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            }
        >
            <div className="space-y-6 mt-6 relative z-10">
                {/* Summary */}
                {theme.summary && (
                    <p className="text-sm text-muted-foreground leading-relaxed italic border-l-2 border-primary/20 pl-4 py-1">
                        {theme.summary}
                    </p>
                )}

                {/* Description */}
                <div className="space-y-1.5 px-1">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60">Cultural Context</div>
                    <p className="text-sm text-foreground/70 font-light leading-relaxed">{theme.description}</p>
                </div>

                {/* Relevance */}
                {theme.relevance && (
                    <div className="space-y-2 px-1">
                        <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/60 flex items-center gap-2">
                            <Sparkles className="h-3 w-3" />
                            Modern Relevance
                        </div>
                        <p className="text-sm text-foreground/70 font-light leading-relaxed italic border-l-2 border-primary/20 pl-4 py-0.5">{theme.relevance}</p>
                    </div>
                )}

                {/* Footer Metadata */}
                <div className="flex flex-wrap items-center justify-between gap-4 pt-5 border-t border-border/10">
                    <div className="flex items-center gap-3 flex-wrap">
                        {theme.importance && (
                            <ImportanceBadge importance={theme.importance} context="cultural" />
                        )}
                        {theme.confidence && (
                            <ConfidenceBadge confidence={theme.confidence} context="general" />
                        )}
                    </div>

                    {theme.sources?.length > 0 && (
                        <SourceLinks sources={theme.sources} />
                    )}
                </div>
            </div>
        </DiscoveryCard>
    );
}
